import { useEffect, useRef } from 'react';
import { Helmet } from "react-helmet-async";
import { Container, Box, Typography } from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import GrassIcon from '@mui/icons-material/Grass';
import YardIcon from '@mui/icons-material/Yard';
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import CtaSection from '../components/cta-section/CtaSection.tsx';
import './servizi.scss';

gsap.registerPlugin(ScrollTrigger);

function Servizi() {
  const heroRef = useRef<HTMLDivElement>(null);
  const serviziTitleRef = useRef<HTMLHeadingElement>(null);
  const prodottiTitleRef = useRef<HTMLHeadingElement>(null);
  const stripeImagesRef = useRef<HTMLDivElement[]>([]);
  const prodottiGridRef = useRef<HTMLDivElement>(null);

  // Split Text Animation
  const animateSplitText = (element: HTMLElement) => {
    const text = element.textContent || '';
    element.innerHTML = text
      .split('')
      .map(char => char === ' ' ? ' ' : `<span class="split-char">${char}</span>`)
      .join('');

    const chars = element.querySelectorAll('.split-char');
    gsap.fromTo(chars,
      { y: 50, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        duration: 0.6,
        stagger: 0.03,
        ease: "power3.out",
        scrollTrigger: {
          trigger: element,
          start: "top 80%",
          toggleActions: "play none none none"
        }
      }
    );
  };

  useEffect(() => {
    if (heroRef.current) {
      gsap.fromTo(heroRef.current,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 1, ease: "power2.out" }
      );
    }

    // Animate split text titles
    if (serviziTitleRef.current) {
      animateSplitText(serviziTitleRef.current);
    }
    if (prodottiTitleRef.current) {
      animateSplitText(prodottiTitleRef.current);
    }

    // Animate product cards fade-in
    if (prodottiGridRef.current) {
      const cards = prodottiGridRef.current.querySelectorAll('.prodotto-card');
      gsap.fromTo(cards,
        { y: 60, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.2,
          ease: "power3.out",
          scrollTrigger: {
            trigger: prodottiGridRef.current,
            start: "top 80%",
            toggleActions: "play none none none"
          }
        }
      );
    }

    // Animate stripe images reveal (wipe effect: 100% -> 0 -> -100%)
    stripeImagesRef.current.forEach((el) => {
      if (el) {
        const overlay = el.querySelector('.stripe-overlay');
        if (overlay) {
          const tl = gsap.timeline({
            scrollTrigger: {
              trigger: el,
              start: "top 80%",
              toggleActions: "play none none none"
            }
          });

          tl.to(overlay, {
            x: '0%',
            duration: 0.5,
            ease: "power2.inOut"
          })
          .to(overlay, {
            x: '-100%',
            duration: 0.5,
            ease: "power2.inOut",
            delay: 0.15
          });
        }
      }
    });

    // Refresh ScrollTrigger dopo la transizione
    const refreshTimer = setTimeout(() => {
      ScrollTrigger.refresh();
    }, 600);

    return () => {
      clearTimeout(refreshTimer);
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <>
      <Helmet>
        <title>Servizi | Fabbri Agricoltura</title>
        <meta name="description" content="Servizi di Fabbri Agricoltura: vendita prodotti selezionati per l'agricoltura e consulenza sul campo." />
        <meta name="keywords" content="vendita prodotti agricoli, consulenza agricola, fabbri agricoltura" />
      </Helmet>

      <Box className="servizi-page-agricola">
        {/* Hero Section */}
        <Box
          className="servizi-hero-agricola"
          ref={heroRef}
        >
          <video
            className="hero-video-agricola"
            autoPlay
            loop
            muted
            playsInline
          >
            <source src="/video/video2.mp4" type="video/mp4" />
          </video>
          <Box className="hero-overlay-agricola"></Box>
          <Container maxWidth="lg" className="hero-content-agricola">
            <Box className="hero-box-agricola">
              {/* <Typography variant="overline" className="hero-label-agricola">
                I Servizi
              </Typography> */}
              <Typography variant="h1" className="hero-title-agricola">
                Servizi Agricoli <br></br> Professionali
              </Typography>
              <Typography className="hero-description-agricola">
                Prodotti selezionati e consulenza professionale per l'agricoltura
              </Typography>
            </Box>
          </Container>
        </Box>

        {/* Services Section */}
        <Box className="servizi-section">
          <Container maxWidth="lg">
            <Typography variant="h2" className="section-title-agricola split-text" ref={serviziTitleRef}>
              I Servizi
            </Typography>

            {/* Servizio 1: Consulenza - Immagine a sinistra */}
            <Box className="servizio-stripe">
              <Box className="stripe-image" ref={(el: HTMLDivElement) => { stripeImagesRef.current[0] = el; }}>
                <img src="/sfondi/sfondo2.png" alt="Consulenza" />
                <Box className="stripe-overlay" />
              </Box>
              <Box className="stripe-content">
                <Typography variant="h3" className="stripe-title">
                  Consulenza
                </Typography>
                <Typography className="stripe-description">
                  Consulenza professionale direttamente in campo. Sarai affiancato nella scelta delle migliori soluzioni per ottimizzare la produzione e risolvere ogni problematica agronomica per la coltivazione di seminativi, frutteti, vigneti e colture orticole
                </Typography>
              </Box>
            </Box>

            {/* Servizio 2: Vendita Prodotti - Immagine a destra */}
            <Box className="servizio-stripe stripe-reverse">
              <Box className="stripe-image" ref={(el: HTMLDivElement) => { stripeImagesRef.current[1] = el; }}>
                <img src="/sfondi/sfondo1.png" alt="Vendita Prodotti" />
                <Box className="stripe-overlay" />
              </Box>
              <Box className="stripe-content">
                <Typography variant="h3" className="stripe-title">
                  Vendita Prodotti
                </Typography>
                <Typography className="stripe-description">
                  Selezione accurata di mezzi tecnici per l'agricoltura, dalla semina alla raccolta, tutto ciò di cui hai bisogno per le tue colture.
                </Typography>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* Prodotti Section */}
        <Box className="prodotti-section">
          <Container maxWidth="lg">
            <Typography variant="h2" className="section-title-agricola split-text" ref={prodottiTitleRef}>
              I Prodotti
            </Typography>

            <Box className="prodotti-grid" ref={prodottiGridRef}>
              <Box className="prodotto-card">
                <Box className="prodotto-icon">
                  <ScienceIcon sx={{ fontSize: 40 }} />
                </Box>
                <Typography variant="h6" className="prodotto-title">
                  Agrofarmaci
                </Typography>
                <Typography className="prodotto-desc">
                  Fitofarmaci selezionati per offrire protezione, qualità e risultati concreti per le tue colture.                
                </Typography>
              </Box>

              <Box className="prodotto-card">
                <Box className="prodotto-icon">
                  <GrassIcon sx={{ fontSize: 40 }} />
                </Box>
                <Typography variant="h6" className="prodotto-title">
                  Sementi
                </Typography>
                <Typography className="prodotto-desc">
                  Ampia disponibilità tra le migliori sementi, selezionate sulla base di una ricerca continua e riscontri pratici in campo.
                </Typography>
              </Box>

              <Box className="prodotto-card">
                <Box className="prodotto-icon">
                  <YardIcon sx={{ fontSize: 40 }} />
                </Box>
                <Typography variant="h6" className="prodotto-title">
                  Concimi e biostimolanti
                </Typography>
                <Typography className="prodotto-desc">
                  Dalla radice alla chioma, energia per ogni pianta.
                  Concimi e biostimolanti per una crescita sana e funzionale delle colture.
                </Typography>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* CTA Section */}
        <CtaSection />
      </Box>
    </>
  );
}

export default Servizi;
